import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '+971 56 683 2840',
      href: 'tel:+971566832840',
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'info@glow-space.com',
      href: 'mailto:info@glow-space.com',
    },
    {
      icon: MapPin,
      title: 'Head Office (Dubai)',
      value: 'Champion Tower, Dubai Sports City, Dubai, UAE',
      href: 'https://maps.google.com/?q=Champion+Tower+Dubai+Sports+City',
    },
    {
      icon: MapPin,
      title: 'Showroom (Al Quoz)',
      value: 'Al Quoz, Dubai, UAE',
      href: 'https://maps.google.com/?q=Al+Quoz+Dubai+UAE',
    },
    {
      icon: MapPin,
      title: 'Al Ain Office',
      value: 'Asharej, Al Ain, UAE',
      href: 'https://maps.google.com/?q=Asharej+Al+Ain+UAE',
    },
    {
      icon: MapPin,
      title: 'Workshop (Sharjah)',
      value: 'Maliha, Sharjah, UAE',
      href: 'https://maps.google.com/?q=Maliha+Sharjah+UAE',
    },
    {
      icon: Clock,
      title: 'Working Hours',
      value: 'Sunday - Thursday: 8AM - 6PM',
      href: null,
    },
  ];

  const handleSubmit = () => {
    setIsSubmitting(true);
    const whatsappMessage = `Hello, I'm ${formState.name || 'interested'}. I need help with ${formState.service || 'your services'}. ${formState.message || ''}`;
    setTimeout(() => {
      window.open(`https://wa.me/971566832840?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
      setIsSubmitting(false);
    }, 500);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section 
      id="contact" 
      className="py-24 md:py-32 px-6 relative"
      data-testid="contact-section"
      ref={ref}
    >
      {/* Glassy background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-white/60 backdrop-blur-sm"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <motion.span 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-sky-600 block mb-4 flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="w-8 h-px bg-gradient-to-r from-sky-500 to-cyan-400"></span>
              Get In Touch
            </motion.span>
            <motion.h2 
              className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-800 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Let's Discuss <br/>
              Your Project
            </motion.h2>
            <motion.p 
              className="text-slate-600 text-lg leading-relaxed mb-10"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Ready to start your next project? Contact us for a free consultation and quote. We're here to help bring your vision to life.
            </motion.p>

            {/* Contact Details */}
            <motion.div 
              className="space-y-6 mb-10"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="flex items-start gap-4 group"
                  data-testid={`contact-${item.title.toLowerCase()}`}
                  variants={itemVariants}
                  whileHover={{ x: 10 }}
                >
                  <motion.div 
                    className="w-12 h-12 rounded-xl glass-card flex items-center justify-center flex-shrink-0 group-hover:bg-white/90 transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <item.icon size={20} className="text-sky-600 group-hover:text-[#25D366] transition-colors" />
                  </motion.div>
                  <div>
                    <div className="text-sm text-slate-500 mb-1">{item.title}</div>
                    {item.href ? (
                      <a 
                        href={item.href}
                        target={item.href.startsWith('https') ? '_blank' : undefined}
                        rel={item.href.startsWith('https') ? 'noopener noreferrer' : undefined}
                        className="text-slate-800 hover:text-[#25D366] transition-colors font-medium"
                        data-testid={`contact-link-${item.title.toLowerCase()}`}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-slate-800 font-medium">{item.value}</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* WhatsApp CTA */}
            <motion.a
              href="https://wa.me/971566832840?text=Hello%2C%20I%27m%20interested%20in%20your%20services"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp inline-flex group"
              data-testid="contact-whatsapp-btn"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
              whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(37, 211, 102, 0.4)' }}
              whileTap={{ scale: 0.98 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Chat on WhatsApp
              <motion.span
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                →
              </motion.span>
            </motion.a>
          </motion.div>

          {/* Right - Quick Contact Form */}
          <motion.div 
            className="glass-card p-8 rounded-3xl"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <h3 
              className="text-xl font-medium text-slate-800 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Send Us a Message
            </h3>
            <form className="space-y-6" data-testid="contact-form" onSubmit={(e) => e.preventDefault()}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <label className="block text-sm text-slate-500 mb-2">Your Name</label>
                <motion.input
                  type="text"
                  className="contact-input"
                  placeholder="John Smith"
                  data-testid="contact-name-input"
                  value={formState.name}
                  onChange={(e) => setFormState({...formState, name: e.target.value})}
                  whileFocus={{ borderColor: 'rgba(37, 211, 102, 0.5)' }}
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.35 }}
              >
                <label className="block text-sm text-slate-500 mb-2">Email Address</label>
                <motion.input
                  type="email"
                  className="contact-input"
                  placeholder="john@example.com"
                  data-testid="contact-email-input"
                  value={formState.email}
                  onChange={(e) => setFormState({...formState, email: e.target.value})}
                  whileFocus={{ borderColor: 'rgba(37, 211, 102, 0.5)' }}
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <label className="block text-sm text-slate-500 mb-2">Phone Number</label>
                <motion.input
                  type="tel"
                  className="contact-input"
                  placeholder="+971 50 000 0000"
                  data-testid="contact-phone-input"
                  value={formState.phone}
                  onChange={(e) => setFormState({...formState, phone: e.target.value})}
                  whileFocus={{ borderColor: 'rgba(37, 211, 102, 0.5)' }}
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.45 }}
              >
                <label className="block text-sm text-slate-500 mb-2">Service Interested In</label>
                <motion.select 
                  className="contact-input cursor-pointer"
                  data-testid="contact-service-select"
                  value={formState.service}
                  onChange={(e) => setFormState({...formState, service: e.target.value})}
                  whileFocus={{ borderColor: 'rgba(37, 211, 102, 0.5)' }}
                >
                  <option value="">Select a service</option>
                  <option value="aluminium">Aluminium Works</option>
                  <option value="glass">Glass Works</option>
                  <option value="drywall">Drywall Installation</option>
                  <option value="flooring">Flooring Services</option>
                  <option value="painting">Painting Services</option>
                  <option value="cleaning">Cleaning Services</option>
                </motion.select>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <label className="block text-sm text-slate-500 mb-2">Message</label>
                <motion.textarea
                  className="contact-input min-h-[120px] resize-none"
                  placeholder="Tell us about your project..."
                  data-testid="contact-message-input"
                  value={formState.message}
                  onChange={(e) => setFormState({...formState, message: e.target.value})}
                  whileFocus={{ borderColor: 'rgba(37, 211, 102, 0.5)' }}
                ></motion.textarea>
              </motion.div>
              <motion.button
                type="button"
                onClick={handleSubmit}
                className="btn-primary w-full group relative overflow-hidden"
                data-testid="contact-submit-btn"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.55 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
              >
                <span className={`flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-0' : 'opacity-100'}`}>
                  <Send size={18} />
                  Send via WhatsApp
                </span>
                {isSubmitting && (
                  <motion.span 
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <CheckCircle size={24} className="text-[#25D366]" />
                  </motion.span>
                )}
              </motion.button>
              <p className="text-xs text-slate-500 text-center">
                Your message will be sent directly to our WhatsApp for faster response.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
